import BudgetsFormCst from "../BudgetsFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box, Container } from "@mui/material";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";

const EditBudgets: React.FC = () => {
  const { t } = useTranslation(["budgets-page"]);
  const { idBudget, id } = useParams();
  const navigate = useNavigate();

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getBudgets}`,
    idBudget,
    PagesID["budget"],
  );

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerEditBudget } = useSWRMutation(
    `${ServicesURL.postBudget}/${idBudget}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.budget.messageLoading"),
        title: t("edit.outcome.budget.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.budget.messageLoading"),
      title: t("edit.outcome.budget.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));
    const objToSend: any = {
      year: Number(inputForm.year) || null,
      cost_center_id: Number(inputForm.cost_center) || null,
      account_id: Number(inputForm.account) || null,
      allocated_budget: Number(inputForm.allocated_budget) || null,
      forecast1: Number(inputForm.forecast1) || null,
      forecast2: Number(inputForm.forecast2) || null,
      forecast3: Number(inputForm.forecast3) || null,
      notes: inputForm.notes || null,
    };

    try {
      await triggerEditBudget(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.budget.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (
        el.accessorKey === "account_id" ||
        el.accessorKey === "cost_center_id" ||
        el.accessorKey === "year"
      ) {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, [columns]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-budget.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {getColumns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <BudgetsFormCst
              row={dataDetail?.dto || []}
              columns={getColumns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              fullWidth={true}
              isEdit={true}
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              id
                ? `/corporate/${id}/detail/budget-management/budgets`
                : `/budget-management/budgets`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditBudgets;
